import { Alert, AlertTitle, Description, List, LottiePlayer, MainTitle, Svg } from '@repo/ui';
import { useState } from 'react';

import { useLookupResultRefundData } from '@/components/tax-refund/lookup/result/LookupResultContext';
import numberWithCommas from '@/lib/utils/numberWithCommas';

interface Props {
  userName: string;
  businessType: 'indis' | 'corps';
  refundAmount: number;
  tritxRefundAmount: number;
}

export const UserRefundAmount = ({ userName, businessType, refundAmount, tritxRefundAmount }: Props) => {
  const isIncomeFirst = refundAmount >= tritxRefundAmount;

  return (
    <>
      <section className={'py-normal-large bg-[linear-gradient(180deg,var(--neutral-background-main,#fff)_0%,var(--info-background-low,#e9f3ff)_60%)]'}>
        <div className={'max-w-[520px] mx-auto page-padding'}>
          <MainTitle
            className={'break-all text-center'}
            title={`${businessType === 'corps' ? `'${userName}'의` : `${userName}님의`}\n예상 환급액이에요`}
            size={'large'}
          />
          <Description
            size={'medium'}
            className={'py-0'}
            graphic={<LottiePlayer src={'/lotties/result_coin-lottie.json'} className={'w-[312px] h-[176.8px]'} />}
            description={
              <p className={'text-[16px] text-gray-700 flex flex-col justify-center py-[16px] gap-[4px] -mt-[104px]'}>
                <b className={'headline-20 text-gray-900 inline-block mb-1'}>{numberWithCommas(Number(refundAmount) + Number(tritxRefundAmount))}원</b>
                받을 수 있어요!
              </p>
            }
          />
          <div className={'flex flex-col items-start gap-[12px] self-stretch'}>
            {businessType === 'indis' && (
              <>
                {isIncomeFirst ? (
                  <>
                    <IncomeCard refundAmount={refundAmount} />
                    <TritxCard tritxRefundAmount={tritxRefundAmount} />
                  </>
                ) : (
                  <>
                    <TritxCard tritxRefundAmount={tritxRefundAmount} />
                    <IncomeCard refundAmount={refundAmount} />
                  </>
                )}
              </>
            )}
            <Alert
              className={'p-normal-small bg-info-background-low'}
              graphic={<Svg src={'icg_info_sm.svg'} width={20} height={20} />}
              variant="custom"
              size="small"
            >
              <AlertTitle className="grow break-all text-gray-800 body-13">
                지난 5년간 공제 및 감면 혜택을 제대로 적용하지 못해 더 많이 납부했다면 환급액이 발생해요.
              </AlertTitle>
            </Alert>
          </div>
        </div>
      </section>
    </>
  );
};

interface IncomeCardProps {
  refundAmount: number;
}

const IncomeCard = ({ refundAmount }: IncomeCardProps) => {
  const { snmcSpcTxreAmt, cenSnmcTxreAmt, empIncrEtrpTxamtDdcAmt, intgEmpTxamtDdcAmt, sctyInfeeTxamtDdcAmt } = useLookupResultRefundData();
  const [isTaxCardOpen, setIsTaxCardOpen] = useState(false);
  const hasTaxDeductionDetails = snmcSpcTxreAmt > 0 || cenSnmcTxreAmt > 0 || empIncrEtrpTxamtDdcAmt + intgEmpTxamtDdcAmt > 0 || sctyInfeeTxamtDdcAmt > 0;

  if (refundAmount <= 0) {
    return null;
  }

  return (
    <div
      className={`flex flex-col items-start self-stretch px-[24px] py-[4px] rounded-[16px] bg-white ${hasTaxDeductionDetails ? 'cursor-pointer' : ''}`}
      onClick={hasTaxDeductionDetails ? () => setIsTaxCardOpen((prev) => !prev) : undefined}
    >
      <List
        size={'medium'}
        title={'종합소득세 환급'}
        className={hasTaxDeductionDetails ? 'cursor-pointer' : ''}
        leadingElement={<Svg width={32} height={32} src={'icg_won-arrow-counterclockwise_md.svg'} />}
        secondaryAction={
          hasTaxDeductionDetails ? <Svg src={`ic_chevron-${isTaxCardOpen ? 'up' : 'down'}.svg`} width={20} height={20} color={'#8A96A3'} /> : null
        }
        description={`${numberWithCommas(refundAmount)}원`}
      />
      {isTaxCardOpen && (
        <div className={'flex flex-col items-start self-stretch pt-[4px] pb-0 border-t border-t-[rgba(0,30,57,0.06)]'}>
          {snmcSpcTxreAmt > 0 && (
            <List
              size={'small'}
              title={'중소기업 특별감면'}
              supportingElement={<div className={'text-[14px]'}>{`${numberWithCommas(snmcSpcTxreAmt)}원`}</div>}
            />
          )}
          {cenSnmcTxreAmt > 0 && (
            <List
              size={'small'}
              title={'창업중소기업 세액감면'}
              supportingElement={<div className={'text-[14px]'}>{`${numberWithCommas(cenSnmcTxreAmt)}원`}</div>}
            />
          )}
          {empIncrEtrpTxamtDdcAmt + intgEmpTxamtDdcAmt > 0 && (
            <List
              size={'small'}
              title={'고용증대 세액공제'}
              supportingElement={<div className={'text-[14px]'}>{`${numberWithCommas(empIncrEtrpTxamtDdcAmt + intgEmpTxamtDdcAmt)}원`}</div>}
            />
          )}
          {sctyInfeeTxamtDdcAmt > 0 && (
            <List
              size={'small'}
              title={'사회보험료 세액공제'}
              supportingElement={<div className={'text-[14px]'}>{`${numberWithCommas(sctyInfeeTxamtDdcAmt)}원`}</div>}
            />
          )}
        </div>
      )}
    </div>
  );
};

interface TritxCardProps {
  tritxRefundAmount: number;
}

const TritxCard = ({ tritxRefundAmount }: TritxCardProps) => {
  const isPersonalTritx = sessionStorage.getItem('isPersonalTritx') === 'true';

  if (tritxRefundAmount <= 0 && !isPersonalTritx) {
    return null;
  }

  return (
    <div className={'flex flex-col items-start self-stretch px-[24px] py-[4px] rounded-[16px] bg-white'}>
      <List
        size={'medium'}
        title={'양도소득세 환급'}
        leadingElement={<Svg width={32} height={32} src={'icg_won-arrow-up_md.svg'} />}
        description={`${numberWithCommas(tritxRefundAmount)}원`}
      />
    </div>
  );
};
